import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'
import I18n from 'APP/I18n'
import { validateIsChild } from 'APP/Lib/DateHelpers'
import { isWeb } from 'APP/Helpers/checkPlatform'
import { AddDependentFormData } from '../types'
import { FormStep } from '../types'

export type UseAddDependentFormReturn = UseFormReturn<AddDependentFormData> & {
  dateOfBirthLabel: string
  dateOfBirthValue: string | undefined
  hasErrors: boolean
  isChild: boolean
  isAdult: boolean
  isFormValid: boolean
  focusedField: string | null
  setFocusedField: (field: string | null) => void
  currentStep: FormStep
  setCurrentStep: (step: FormStep) => void
}

/**
 * Custom hook for managing the Add Dependent form state and logic.
 *
 * Handles:
 * - Form validation and state management via react-hook-form
 * - Dependent type determination (child vs adult) based on date of birth
 * - Conditional field requirements and validation
 * - Form step calculation for multi-step flow
 * - Field cleanup when dependent type changes
 *
 * @returns Extended form methods with additional computed state
 */

export const useAddDependentForm = (): UseAddDependentFormReturn => {
  const [focusedField, setFocusedField] = useState<string | null>(null)
  const [currentStep, setCurrentStep] = useState<FormStep>(FormStep.INITIAL)
  const hookForm = useForm<AddDependentFormData>({
    mode: 'all',
  })

  const {
    watch,
    setValue,
    formState: { errors },
  } = hookForm

  const dateOfBirthLabel = isWeb() ? I18n.t('ProfileScreen.bdayWeb') : I18n.t('ProfileScreen.bday')
  const { dateOfBirth: dateOfBirthValue, firstName, lastName, email } = watch()
  const hasErrors = Object.keys(errors).length > 0
  const hasDobError = !!errors.dateOfBirth

  const hasFieldError = useCallback(
    (field: keyof AddDependentFormData) => !!errors[field],
    [errors]
  )

  const isChild = useMemo(
    () => !hasDobError && !!dateOfBirthValue && validateIsChild(dateOfBirthValue),
    [dateOfBirthValue, hasDobError]
  )

  const isAdult = useMemo(
    () => !!dateOfBirthValue && !isChild && !hasDobError,
    [dateOfBirthValue, isChild, hasDobError]
  )

  // Clear irrelevant fields when dependent type changes
  useEffect(() => {
    if (isAdult) {
      if (firstName) setValue('firstName', '')
      if (lastName) setValue('lastName', '')
    }
    if (isChild && email) {
      setValue('email', '')
    }
  }, [isChild, isAdult, firstName, lastName, email, setValue])

  // We are manually checking the form validity because the react-hook-form has a bug with conditional fields.
  const isFormValid = useMemo(() => {
    if (hasErrors) return false

    if (isChild) {
      return (
        !!dateOfBirthValue &&
        !!firstName &&
        !!lastName &&
        !hasFieldError('firstName') &&
        !hasFieldError('lastName')
      )
    }

    if (isAdult) {
      return !!dateOfBirthValue && !!email && !hasFieldError('email')
    }

    return !!dateOfBirthValue
  }, [hasErrors, isChild, isAdult, dateOfBirthValue, firstName, lastName, hasFieldError, email])

  useEffect(() => {
    if (currentStep === FormStep.GET_CONSENT) {
      return
    }

    if (isAdult) {
      setCurrentStep(FormStep.INVITE_ADULT)
      return
    }

    if (isChild) {
      setCurrentStep(FormStep.ADD_CHILD)
      return
    }

    setCurrentStep(FormStep.INITIAL)
  }, [isAdult, isChild, currentStep])

  return {
    ...hookForm,
    dateOfBirthLabel,
    dateOfBirthValue,
    hasErrors,
    isChild,
    isAdult,
    isFormValid,
    focusedField,
    setFocusedField,
    currentStep,
    setCurrentStep,
  }
}
